import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withModifiers as _withModifiers, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "k-uploader-carousel column no-wrap items-center gap-md full-width relative-position" }
const _hoisted_2 = ["onClick", "onDragstart", "onDrop"]
const _hoisted_3 = {
  key: 0,
  class: "row no-wrap items-center gap-lg"
}
const _hoisted_4 = { class: "row no-wrap items-center gap-12" }
const _hoisted_5 = ["onClick"]

import { computed, reactive, watch } from 'vue'

interface IProps {
  /** Displayable URLs, in order */
  images: string[]
  active: number
}

interface IEmits {
  (e: 'select', index: number): void
  (e: 'reorder', from: number, to: number): void
}

interface IState {
  /** How many slides fit the available width, which is also the page size */
  capacity: number
  /** The page navigated to; read it through view.page, which keeps it in range when images are removed */
  page: number
}

// Mirror the slide size and the gap-12 class in the template/styles.
const SLIDE_WIDTH = 88
const SLIDE_GAP = 12

export default /*@__PURE__*/_defineComponent({
  ...{ name: 'KUploaderCarousel' },
  __name: 'KUploaderCarousel',
  props: {
    images: {},
    active: {}
  },
  emits: ["select", "reorder"],
  setup(__props: any, { emit: __emit }) {

const SLIDE_STEP = SLIDE_WIDTH + SLIDE_GAP


const props = __props
const emit = __emit

const state = reactive<IState>({ capacity: 3, page: 0 })
// Not reactive: nothing renders from it
let draggedIndex: number | null = null

// The paging, worked out in one pass since every value follows from the image count, capacity and page
const view = computed(() => {
  const { capacity } = state
  const pageCount = Math.ceil(props.images.length / capacity)
  const page = Math.min(state.page, pageCount - 1)
  // The last page is pulled back to end on the last slide instead of on empty space
  const firstVisible = Math.min(page * capacity, Math.max(props.images.length - capacity, 0))

  return {
    pageCount,
    page,
    firstVisible,
    offset: firstVisible * SLIDE_STEP,
    width: Math.min(props.images.length, capacity) * SLIDE_STEP - SLIDE_GAP,
  }
})

function goToPage(target: number) {
  state.page = Math.min(Math.max(target, 0), view.value.pageCount - 1)
}

function onResize({ width }: { width: number }) {
  state.capacity = Math.max(Math.floor((width + SLIDE_GAP) / SLIDE_STEP), 1)
  goToPage(Math.floor(props.active / state.capacity))
}

function onDragStart(event: DragEvent, index: number) {
  draggedIndex = index
  if (!event.dataTransfer) return
  event.dataTransfer.effectAllowed = 'move'
  // Firefox doesn't start a drag without data.
  event.dataTransfer.setData('text/plain', String(index))
}

function onDrop(index: number) {
  if (draggedIndex !== null && draggedIndex !== index) emit('reorder', draggedIndex, index)
}

// Fires after the drop, and also when the drag is cancelled, so a stale index can't reorder on a later file drop
function onDragEnd() {
  draggedIndex = null
}

// Follow the active slide (e.g. a newly added image) when it's outside the visible page.
watch(
  () => props.active,
  (active) => {
    const { firstVisible } = view.value
    if (active < firstVisible || active >= firstVisible + state.capacity) goToPage(Math.floor(active / state.capacity))
  },
)

return (_ctx: any,_cache: any) => {
  const _component_q_resize_observer = _resolveComponent("q-resize-observer")!
  const _component_q_img = _resolveComponent("q-img")!
  const _component_KIcon = _resolveComponent("KIcon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_q_resize_observer, { onResize: onResize }),
    _createElementVNode("div", {
      class: "overflow-hidden",
      style: _normalizeStyle({ width: `${view.value.width}px` })
    }, [
      _createElementVNode("div", {
        class: "k-uploader-carousel__track row no-wrap gap-12",
        style: _normalizeStyle({ transform: `translateX(-${view.value.offset}px)` })
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.images, (image, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: image,
            class: _normalizeClass(["k-uploader-carousel__slide relative-position overflow-hidden cursor-pointer rounded-8 transition", { 'k-uploader-carousel__slide--active': index === __props.active }]),
            draggable: "true",
            onClick: ($event: any) => (emit('select', index)),
            onDragstart: ($event: any) => (onDragStart($event, index)),
            onDragover: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"])),
            onDrop: _withModifiers(($event: any) => (onDrop(index)), ["prevent"]),
            onDragend: onDragEnd
          }, [
            _createVNode(_component_q_img, {
              src: image,
              fit: "cover",
              class: "full-height"
            }, null, 8, ["src"]),
            _createElementVNode("div", {
              class: "k-uploader-carousel__handle absolute-left row items-center q-px-xs",
              onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
            }, [
              _createVNode(_component_KIcon, {
                name: "arrangement",
                color: "cool-gray-70"
              })
            ])
          ], 42, _hoisted_2))
        }), 128))
      ], 4)
    ], 4),
    (view.value.pageCount > 1)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(_component_KIcon, {
            name: "arrow-left",
            size: "26px",
            class: "cursor-pointer",
            color: view.value.page > 0 ? 'cool-gray-100' : 'cool-gray-50',
            onClick: _cache[2] || (_cache[2] = ($event: any) => (goToPage(view.value.page - 1)))
          }, null, 8, ["color"]),
          _createElementVNode("div", _hoisted_4, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(view.value.pageCount, (pageNumber) => {
              return (_openBlock(), _createElementBlock("div", {
                key: pageNumber,
                class: _normalizeClass(["k-uploader-carousel__bullet rounded-circle cursor-pointer", pageNumber - 1 === view.value.page ? 'bg-space-b-100' : 'bg-cool-gray-50']),
                onClick: ($event: any) => (goToPage(pageNumber - 1))
              }, null, 10, _hoisted_5))
            }), 128))
          ]),
          _createVNode(_component_KIcon, {
            name: "arrow-right",
            size: "26px",
            class: "cursor-pointer",
            color: view.value.page < view.value.pageCount - 1 ? 'cool-gray-100' : 'cool-gray-50',
            onClick: _cache[3] || (_cache[3] = ($event: any) => (goToPage(view.value.page + 1)))
          }, null, 8, ["color"])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})